/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Conversation-upload DLP copy from spec #423 §5.
// Block sentences may be the server admin override. Unavailable and
// fail-open sentences are fixed. File bodies and matched snippets are ignored.

export const DLP_ERR_BLOCKED = 104000011;
export const DLP_ERR_UNAVAILABLE = 104000012;

export const DLP_REASON_POLICY = 'dlp_policy';
export const DLP_REASON_LABEL = 'sensitivity_label';
export const DLP_REASON_UNAVAILABLE = 'dlp_unavailable';

export const DLP_NOTICE_FAIL_OPEN = 'fail_open';

export const DLP_BLOCK_ZH =
  '该文件被组织的数据保护策略判定为敏感，无法上传到对话。';
export const DLP_BLOCK_EN =
  'This file is classified as sensitive by your organization’s data protection policy and can’t be uploaded to the chat.';
export const DLP_UNAVAILABLE_ZH =
  '数据保护检查暂时不可用，本次上传已被拒绝。请稍后重试或联系管理员。';
export const DLP_UNAVAILABLE_EN =
  'Data protection check is temporarily unavailable; this upload was blocked. Try again later or contact an admin.';
export const DLP_FAIL_OPEN_ZH =
  '数据保护检查暂时不可用，已放行本次上传并通知管理员。';
export const DLP_FAIL_OPEN_EN =
  'Data protection check is temporarily unavailable; this upload was allowed and admins were notified.';

const MAX_MESSAGE_LENGTH = 512;

export type DlpLocale = 'zh' | 'en';
export type DlpKind = 'block' | 'unavailable' | 'fail_open';

export interface DlpUserMessage {
  kind: DlpKind;
  message: string;
  reasonCode: string;
  code?: number;
}

const MESSAGE_KEYS = new Set(['msg', 'message', 'Message']);
const CODE_KEYS = new Set(['code', 'error_code', 'errorCode', 'CodeN']);
const REASON_KEYS = new Set(['reason_code', 'reasonCode', 'Code']);
const NOTICE_KEYS = new Set(['dlp_notice', 'dlpNotice']);
const WALK_KEYS = [
  'extra',
  'error',
  'response',
  'data',
  'result',
  'ResponseMetadata',
  'xhr',
  'Error',
];

export const dlpLocale = (language?: string): DlpLocale => {
  const value = (language || '').toLowerCase();
  if (value.startsWith('zh')) {
    return 'zh';
  }
  return 'en';
};

export const dlpDefaultMessage = (
  kind: DlpKind,
  locale: DlpLocale,
): string => {
  if (kind === 'unavailable') {
    return locale === 'zh' ? DLP_UNAVAILABLE_ZH : DLP_UNAVAILABLE_EN;
  }
  if (kind === 'fail_open') {
    return locale === 'zh' ? DLP_FAIL_OPEN_ZH : DLP_FAIL_OPEN_EN;
  }
  return locale === 'zh' ? DLP_BLOCK_ZH : DLP_BLOCK_EN;
};

const isPublicReason = (value: string): boolean =>
  value === DLP_REASON_POLICY ||
  value === DLP_REASON_LABEL ||
  value === DLP_REASON_UNAVAILABLE;

const isKnownDlpSentence = (value: string): boolean =>
  value === DLP_BLOCK_ZH ||
  value === DLP_BLOCK_EN ||
  value === DLP_UNAVAILABLE_ZH ||
  value === DLP_UNAVAILABLE_EN ||
  value === DLP_FAIL_OPEN_ZH ||
  value === DLP_FAIL_OPEN_EN;

const isGenericUploaderMessage = (value: string): boolean =>
  value.startsWith('Parse response error') ||
  value.startsWith('Parse server response error') ||
  value.startsWith('Request failed') ||
  value.startsWith('Upload failed') ||
  value.startsWith('HttpStatus:') ||
  value.startsWith('Request timeout');

export const isSafeDlpMessage = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed || [...trimmed].length > MAX_MESSAGE_LENGTH) {
    return false;
  }
  if (isGenericUploaderMessage(trimmed)) {
    return false;
  }
  if (/[<>]/.test(trimmed) || /[\u0000-\u001F\u007F]/.test(trimmed)) {
    return false;
  }
  return true;
};

interface Collected {
  code?: number;
  reason?: string;
  message?: string;
  failOpen: boolean;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const readCode = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return Number(value);
  }
  return undefined;
};

const consider = (collected: Collected, record: Record<string, unknown>) => {
  for (const [key, value] of Object.entries(record)) {
    if (CODE_KEYS.has(key) && collected.code === undefined) {
      const code = readCode(value);
      if (
        code === DLP_ERR_BLOCKED ||
        code === DLP_ERR_UNAVAILABLE
      ) {
        collected.code = code;
      }
    }
    if (REASON_KEYS.has(key) && typeof value === 'string' && isPublicReason(value)) {
      collected.reason = value;
    }
    if (
      key === 'error' &&
      typeof value === 'string' &&
      isPublicReason(value)
    ) {
      collected.reason = value;
    }
    if (NOTICE_KEYS.has(key) && value === DLP_NOTICE_FAIL_OPEN) {
      collected.failOpen = true;
    }
    if (
      MESSAGE_KEYS.has(key) &&
      typeof value === 'string' &&
      collected.message === undefined &&
      isSafeDlpMessage(value)
    ) {
      collected.message = value.trim();
    }
  }
};

const readHeader = (
  xhr: Record<string, unknown>,
  name: string,
): string | undefined => {
  const getter = xhr.getResponseHeader;
  if (typeof getter !== 'function') {
    return undefined;
  }
  const value = getter.call(xhr, name);
  return typeof value === 'string' ? value : undefined;
};

const walk = (value: unknown, collected: Collected, depth: number) => {
  if (depth > 4 || value == null) {
    return;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        walk(JSON.parse(trimmed) as unknown, collected, depth + 1);
      } catch {
        return;
      }
      return;
    }
    if (collected.message === undefined && isKnownDlpSentence(trimmed)) {
      collected.message = trimmed;
    }
    return;
  }
  const record = asRecord(value);
  if (!record) {
    return;
  }
  consider(collected, record);
  const notice = readHeader(record, 'X-Dlp-Notice');
  if (notice === DLP_NOTICE_FAIL_OPEN) {
    collected.failOpen = true;
  }
  const reason = readHeader(record, 'X-Dlp-Reason-Code');
  if (reason && isPublicReason(reason)) {
    collected.reason = reason;
  }
  const responseText = record.response;
  if (typeof responseText === 'string') {
    walk(responseText, collected, depth + 1);
  }
  for (const key of WALK_KEYS) {
    if (key in record) {
      walk(record[key], collected, depth + 1);
    }
  }
};

const kindFrom = (collected: Collected): DlpKind | undefined => {
  if (collected.code === DLP_ERR_BLOCKED) {
    return 'block';
  }
  if (
    collected.reason === DLP_REASON_POLICY ||
    collected.reason === DLP_REASON_LABEL
  ) {
    return 'block';
  }
  if (collected.failOpen) {
    return 'fail_open';
  }
  if (
    collected.code === DLP_ERR_UNAVAILABLE ||
    collected.reason === DLP_REASON_UNAVAILABLE
  ) {
    return 'unavailable';
  }
  if (collected.message === DLP_BLOCK_ZH || collected.message === DLP_BLOCK_EN) {
    return 'block';
  }
  if (
    collected.message === DLP_UNAVAILABLE_ZH ||
    collected.message === DLP_UNAVAILABLE_EN
  ) {
    return 'unavailable';
  }
  if (
    collected.message === DLP_FAIL_OPEN_ZH ||
    collected.message === DLP_FAIL_OPEN_EN
  ) {
    return 'fail_open';
  }
  return undefined;
};

const reasonFor = (kind: DlpKind, collected: Collected): string => {
  if (kind === 'block') {
    return collected.reason === DLP_REASON_LABEL
      ? DLP_REASON_LABEL
      : DLP_REASON_POLICY;
  }
  return DLP_REASON_UNAVAILABLE;
};

// resolveDlpUserMessage reads a stable code, reason, or uploader error
// payload and returns the sentence to show. Unknown fields such as file
// contents are not copied into the result.
export const resolveDlpUserMessage = (
  input: unknown,
  language?: string,
): DlpUserMessage | undefined => {
  const collected: Collected = { failOpen: false };
  walk(input, collected, 0);
  const kind = kindFrom(collected);
  if (!kind) {
    return undefined;
  }
  const locale = dlpLocale(language);
  const message =
    kind === 'block' && collected.message
      ? collected.message
      : dlpDefaultMessage(kind, locale);
  return {
    kind,
    message,
    reasonCode: reasonFor(kind, collected),
    code:
      kind === 'block'
        ? DLP_ERR_BLOCKED
        : kind === 'unavailable'
          ? DLP_ERR_UNAVAILABLE
          : undefined,
  };
};
